import { type AirActionTrackingLocation, useTrackEditedBoard } from '@air/analytics';
import { Boards } from '@air/api';
import { type Board } from '@air/api/types';
import { reportErrorToBugsnag } from '@air/utils-error';
import { useMutation } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import { editBoardSuccessAction } from '~/store/centralizedBoard/actions';
import { callAirApi } from '~/swr-hooks/callAirApi';
import { useUpdateBoardInAllLists } from '~/utils/mutateUtils/AllBoards';

export type UsePrivateBoardsSetBoardCoverMutationParams = Parameters<typeof Boards.setCoverImage>[0] & {
  thumbnailUrl?: string;
  board?: Board;
  trackLocation: AirActionTrackingLocation;
};

export const usePrivateBoardsSetBoardCoverMutation = () => {
  const dispatch = useDispatch();
  const { updateBoardInAllLists } = useUpdateBoardInAllLists();
  const { trackEditedBoard } = useTrackEditedBoard();

  return useMutation({
    mutationFn: async (params: UsePrivateBoardsSetBoardCoverMutationParams) => {
      return callAirApi(() =>
        Boards.setCoverImage({
          boardId: params.boardId,
          workspaceId: params.workspaceId,
          clipId: params.clipId,
          region: params.region,
        }),
      );
    },

    onMutate: ({ board, boardId, clipId, region, thumbnailUrl, trackLocation }) => {
      if (board && clipId) {
        const updatedBoard: Board = {
          ...board,
          coverImage: {
            clipId,
            region: {
              // TODO: look into inconsistent types between set/get cover image https://linear.app/air/issue/COR-956/inconsistent-types-w-cover-image-getset
              top: region.top ?? 0,
              left: region.left ?? 0,
              width: region.width ?? 100,
              height: region.height ?? 100,
            },
            url: thumbnailUrl ?? board.coverImage?.url ?? '',
          },
        };

        updateBoardInAllLists(updatedBoard);
        dispatch(editBoardSuccessAction({ board: updatedBoard }));
      }

      trackEditedBoard({ boardId, location: trackLocation });
    },

    onError: (error, variables) => {
      reportErrorToBugsnag({
        error,
        context: 'Failed to set board cover',
        metadata: {
          key: 'data',
          data: variables,
        },
      });
    },
  });
};
